import React, { useCallback, useEffect, useState } from "react"
import { Box, Grid } from "@mui/material"
import { ArticleSection } from "../../../../redux/supportAndGuides/supportArticlesType"
import { PrimaryButton } from "../../../ui/Button"
import { EnhancedField, TextInput } from "../../../ui/Inputs"
import { Form, FormikBag, FormikProps, withFormik } from "formik"
import { CheckboxList } from "../../../ui/CheckboxList"
import { array, object, string } from "yup"
import { RadioButtonsGroup } from "../../../ui/RadioButtonsGroup"
import { ImageUploader } from "../../PhotoUploader"
import { PrimaryText } from "../../../ui/Text"
import { BLACK, GREEN } from "../../../ui/Colors"
import { createNewArticle } from "../../../../redux/supportAndGuides/supportArticlesSlice"
import { useAppDispatch } from "../../../../redux/hooks"
import { BUListToDisplay, inputsArticleForm, sections } from "./utils"
import ErrorMessage from "../../../ui/ErrorMessage"

interface FormValues {
    title: string
    description: string
    BUList: string[]
    link: string
    section: ArticleSection
    image: string
}

interface FormProps {
    onSubmit: (values: FormValues) => void
    initialValues: FormValues
    submitMsg: ValidationStatus
}

interface ValidationStatus {
    status: 'submit' | 'error' | null
    msg: string
}

const PureSignUpJobForm = (
    props: FormikProps<FormValues> & FormProps
): JSX.Element => {

    const handleChangeJobs = (BUSelected: string) => {
        const isInclude = props.values.BUList.includes(BUSelected)

        if (isInclude) {
            props.setFieldValue(
                'BUList',
                props.values.BUList.filter((el) => el !== BUSelected)
            )
        } else {
            props.setFieldValue('BUList', [...props.values.BUList, BUSelected])
        }
    }

    const getUploadName = (fileName: string) => {
        props.setFieldValue('image', fileName)
    }

    return (
        <Form>
            <Grid container direction="column">
                {inputsArticleForm.map((input, index) => (
                    <EnhancedField
                        type={input.type}
                        name={input.name}
                        label={input.label}
                        autoFocus={index === 0}
                        margin="normal"
                        component={TextInput}
                    />
                ))}
                <Grid container justifyContent={"space-between"}>
                    <Grid width="40%">
                        <CheckboxList titleList="BU à qui mettre cet article" list={BUListToDisplay} handleChange={handleChangeJobs} checkedList={props.values.BUList} />
                    </Grid>
                    <Grid mt="2vh" width="40%">
                        <RadioButtonsGroup
                            onChange={(event, value) =>
                                props.setFieldValue('section', value)
                            }
                            values={sections}
                            label={"Rubrique de l'article"}
                            defaultCheck={ArticleSection.FIRST_STEPS}
                        />
                    </Grid>
                </Grid>
                <Grid mt="3vh">
                    <PrimaryText bold color={BLACK} mb="1vh">Ajouter une image</PrimaryText>
                    <ImageUploader getUploadName={getUploadName} />
                </Grid>
                {props.submitMsg &&
                    (props.submitMsg.status === 'submit' ? (
                        <PrimaryText color={GREEN}>
                            {props.submitMsg.msg}
                        </PrimaryText>
                    ) : (
                        <ErrorMessage msg={props.submitMsg.msg} />
                    ))}
                <Box mt="4vh" width="100%">
                    <PrimaryButton fullWidth type="submit">
                        Ajouter l'article
                    </PrimaryButton>
                </Box>
            </Grid>
        </Form>
    )
}

const ControlledSignUpJobForm = withFormik<FormProps, FormValues>({
    mapPropsToValues: (props: FormProps) => ({
        title: props.initialValues.title,
        description: props.initialValues.description,
        BUList: props.initialValues.BUList,
        link: props.initialValues.link,
        section: props.initialValues.section,
        image: props.initialValues.image,
    }),
    validationSchema: () => {
        return object().shape({
            BUList: array()
                .of(string())
                .min(1, `Merci de cocher au moins une BU`),
            title: string().required('Le champs Titre est obligatoire'),
            description: string().required('Le champs description est obligatoire'),
            link: string().required('Le champs link est obligatoire'),
            section: string().required('Le champs Rubrique est obligatoire'),
            image: string().required('Le champs Image est obligatoire'),

        })
    },
    enableReinitialize: true,
    handleSubmit: (
        values: FormValues,
        bag: FormikBag<FormProps, FormValues>
    ) => {
        bag.props.onSubmit(values)
    },
})(PureSignUpJobForm)

export const AddNewArticle = () => {
    const dispatch = useAppDispatch()
    const initialStatus = {
        status: null,
        msg: '',
    }
    const [submitMsg, setSubmitMsg] = useState<ValidationStatus>(initialStatus)

    useEffect(() => {
        if (submitMsg.status === 'submit') {
            const interval = setInterval(() => {
                setSubmitMsg(initialStatus)
            }, 3500)
            return () => {
                clearInterval(interval)
            }
        }
    }, [submitMsg.status])

    const initialValues = {
        title: "",
        description: "",
        BUList: [],
        link: "",
        section: ArticleSection.FIRST_STEPS,
        image: "",
    }
    const _createNewArticle = useCallback(
        async (formData) =>
            await dispatch(createNewArticle(formData))
        ,
        [dispatch]
    )
    const handleSubmit = async (values: FormValues) => {
        let articleAdded = null

        articleAdded = await _createNewArticle(values)

        if (articleAdded) {
            setSubmitMsg({
                status: 'submit',
                msg: 'Votre article a bien été enregistré !',
            })
        } else {
            setSubmitMsg({
                status: 'error',
                msg: 'Erreur lors de l\'ajout de l\'article !',
            })
        }
    }

    return (
        <Grid container direction="column">
            <ControlledSignUpJobForm
                initialValues={initialValues}
                onSubmit={handleSubmit}
                submitMsg={submitMsg}
            />
        </Grid>
    )
}

